import { useState } from 'react';
import './InstrumentList.css';
import { instrumentAPI } from '../../../../../services';
import ConfirmDialog from '../../../../../components/ConfirmDialog/ConfirmDialog';
import TradingHoursModal from './TradingHoursModal';
import QuickPresetModal from './QuickPresetModal';
import React from 'react';

function InstrumentList({ 
  instruments, 
  marketStatus = {},
  onInstrumentUpdated,
  validateSpreadPipettes
}) {
  // Feature flag - disable presets until backend is ready
  const ENABLE_TRADING_HOURS_PRESETS = true;
  
  const [confirmDialog, setConfirmDialog] = useState({
    isOpen: false,
    title: '',
    message: '',
    onConfirm: null
  });

  const [editingInstrument, setEditingInstrument] = useState(null);
  const [selectedInstruments, setSelectedInstruments] = useState([]);
  const [isBackupMode, setIsBackupMode] = useState(false);
  const [showActionMenu, setShowActionMenu] = useState(null);
  const [expandedRows, setExpandedRows] = useState([]);
  const [editingRows, setEditingRows] = useState([]);
  const [editFormData, setEditFormData] = useState({});
  const [editErrors, setEditErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showTradingHoursModal, setShowTradingHoursModal] = useState(null);
  const [showQuickPresetModal, setShowQuickPresetModal] = useState(null);
  const [restoreProgress, setRestoreProgress] = useState({
    isLoading: false,
    current: 0,
    total: 0,
    message: ''
  });
  
  // Bulk endpoint update state
  const [isBulkEndpointMode, setIsBulkEndpointMode] = useState(false);
  const [bulkEndpointValue, setBulkEndpointValue] = useState('');
  const [bulkEndpointError, setBulkEndpointError] = useState('');

  // Helper functions first
  const getSymbolNumber = (symbol) => {
    const match = symbol.match(/^(\d+)/);
    return match ? parseInt(match[1], 10) : Infinity;
  };

  const getCategory = (symbol) => {
    if (symbol.includes('FX.')) return 'FOREX';
    if (symbol.includes('CRYPTO.')) return 'CRYPTO';
    return 'STOCKS';
  };

  const getSymbolWithoutPrefix = (symbol) => {
    const parts = symbol.split('.');
    return parts[parts.length - 1] || symbol;
  };

  // Helper function to get market status badge
  const getMarketStatusBadge = (symbol) => {
    const status = marketStatus[symbol] || 'unknown';
    const statusConfig = {
      open: { 
        className: 'market-status-open', 
        icon: 'fa-clock', 
        text: 'Open',
        title: 'Market is currently open for trading'
      },
      closed: { 
        className: 'market-status-closed', 
        icon: 'fa-pause-circle', 
        text: 'Closed',
        title: 'Market is currently closed'
      },
      holiday: { 
        className: 'market-status-holiday', 
        icon: 'fa-calendar-times', 
        text: 'Holiday',
        title: 'Market is closed due to holiday'
      },
      unknown: { 
        className: 'market-status-unknown', 
        icon: 'fa-question-circle', 
        text: 'Unknown',
        title: 'Market status is unknown'
      }
    };

    const config = statusConfig[status] || statusConfig.unknown;
    
    return (
      <span className={`market-status-badge ${config.className}`} title={config.title}>
        <i className={`fas ${config.icon}`}></i>
        {config.text}
      </span>
    );
  };

  // Handler functions
  const handleDelete = async (instrument) => {
    setShowActionMenu(null);
    setConfirmDialog({
      isOpen: true,
      title: 'Delete Instrument',
      message: `Are you sure you want to delete ${instrument.description}?`,
      onConfirm: async () => {
        try {
          setRestoreProgress({
            isLoading: true,
            current: 1,
            total: 1,
            message: `Deleting ${instrument.symbol}...`
          });
          
          await instrumentAPI.delete(instrument.symbol);
          setConfirmDialog({ isOpen: false });
          
          setTimeout(() => {
            setRestoreProgress({
              isLoading: false,
              current: 0,
              total: 0,
              message: ''
            });
            onInstrumentUpdated();
          }, 500);
        } catch (error) {
          console.error('Error deleting instrument:', error);
          alert('Failed to delete instrument');
          setRestoreProgress({
            isLoading: false,
            current: 0,
            total: 0,
            message: ''
          });
          setConfirmDialog({ isOpen: false });
        }
      }
    });
  };

  const handleDeleteAll = async () => {
    setConfirmDialog({
      isOpen: true,
      title: 'Delete All Instruments',
      message: `Are you sure you want to delete ALL instruments? This action cannot be undone.`,
      onConfirm: () => {
        setConfirmDialog({
          isOpen: true,
          title: 'Final Confirmation Required',
          message: 'This action will delete ALL instruments from the system. This cannot be undone.',
          requireTextConfirmation: true,
          confirmationText: 'confirm',
          onConfirm: async () => {
            try {
              setConfirmDialog({ isOpen: false });
              setRestoreProgress({
                isLoading: true,
                current: 0,
                total: instruments.length,
                message: 'Starting deletion...'
              });

              let successCount = 0;
              let failedInstruments = [];

              for (const [index, instrument] of instruments.entries()) {
                try {
                  setRestoreProgress(prev => ({
                    ...prev,
                    current: index + 1,
                    message: `Deleting ${instrument.symbol}...`
                  }));

                  await instrumentAPI.delete(instrument.symbol);
                  await new Promise(resolve => setTimeout(resolve, 100));
                  successCount++;
                } catch (error) {
                  console.error(`Error deleting instrument ${instrument.symbol}:`, error);
                  failedInstruments.push({
                    symbol: instrument.symbol,
                    reason: error.message || 'API error'
                  });
                }
              }

              setTimeout(() => {
                if (failedInstruments.length > 0) {
                  const failureMessage = failedInstruments
                    .map(f => `${f.symbol}: ${f.reason}`)
                    .join('\n');
                  alert(
                    `Deletion completed with some issues:\n` +
                    `Successfully deleted: ${successCount} instruments\n` +
                    `Failed: ${failedInstruments.length} instruments\n\n` +
                    `Failed instruments:\n${failureMessage}`
                  );
                } else {
                  alert(`Successfully deleted all ${successCount} instruments!`);
                }

                setRestoreProgress({
                  isLoading: false,
                  current: 0,
                  total: 0,
                  message: ''
                });
                onInstrumentUpdated();
              }, 500);

            } catch (error) {
              console.error('Error during deletion:', error);
              alert('Failed to delete all instruments: ' + (error.message || 'Unknown error'));
              setRestoreProgress({
                isLoading: false,
                current: 0,
                total: 0,
                message: ''
              });
            }
          }
        });
      }
    });
  };

  const closeConfirmDialog = () => {
    setConfirmDialog({ isOpen: false });
  };

  const handleEdit = (instrument) => {
    setShowActionMenu(null);
    
    // Expand the row if not already expanded
    if (!expandedRows.includes(instrument._id)) {
      setExpandedRows(prev => [...prev, instrument._id]);
    }
    
    // Set editing mode
    setEditingRows(prev => [...prev, instrument._id]);
    
    // Initialize form data
    setEditFormData(prev => ({
      ...prev,
      [instrument._id]: {
        ...instrument,
        path: Array.isArray(instrument.path) ? instrument.path.join(',') : ''
      }
    }));
    
    setEditErrors(prev => ({ ...prev, [instrument._id]: '' }));
  };

  const handleCancelEdit = (instrumentId) => {
    setEditingRows(prev => prev.filter(id => id !== instrumentId));
    setEditFormData(prev => {
      const newData = { ...prev };
      delete newData[instrumentId];
      return newData;
    });
    setEditErrors(prev => {
      const newErrors = { ...prev };
      delete newErrors[instrumentId];
      return newErrors;
    });
  };

  const handleEditInputChange = (instrumentId, field, value) => {
    if (field === 'spread_pipettes' && value !== '' && !validateSpreadPipettes(value)) {
      return; // Don't update if validation fails
    }
    
    setEditFormData(prev => ({
      ...prev,
      [instrumentId]: {
        ...prev[instrumentId],
        [field]: value
      }
    }));
    
    // Clear error when user starts typing
    if (editErrors[instrumentId]) {
      setEditErrors(prev => ({ ...prev, [instrumentId]: '' }));
    }
  };

  const handleSaveEdit = async (instrumentId) => {
    const formData = editFormData[instrumentId];
    
    if (!validateSpreadPipettes(formData.spread_pipettes)) {
      setEditErrors(prev => ({
        ...prev,
        [instrumentId]: 'Spread pipettes must be a non-negative even number'
      }));
      return;
    }

    setIsSubmitting(true);
    try {
      const submitData = {
        ...formData,
        path: formData.path ? formData.path.split(',').map(p => p.trim()).filter(p => p !== '') : []
      };
      
      await instrumentAPI.update(formData.symbol, submitData);
      
      // Exit edit mode
      handleCancelEdit(instrumentId);
      onInstrumentUpdated();
    } catch (error) {
      setEditErrors(prev => ({
        ...prev,
        [instrumentId]: error.message || 'Failed to update instrument'
      }));
    } finally {
      setIsSubmitting(false);
    }
  };

  // Backup functions
  const handleBackupClick = () => {
    setIsBackupMode(true);
    setSelectedInstruments([]);
  };

  const handleConfirmBackup = () => {
    if (selectedInstruments.length === 0) {
      alert('Please select at least one instrument to backup');
      return;
    }

    const instrumentsToBackup = instruments.filter(
      instrument => selectedInstruments.includes(instrument._id)
    );

    const jsonData = JSON.stringify(instrumentsToBackup, null, 2);
    const blob = new Blob([jsonData], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = 'instruments_backup.json';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
    
    setIsBackupMode(false);
    setSelectedInstruments([]);
  };

  const handleInstrumentSelect = (instrumentId) => {
    setSelectedInstruments(prev => {
      if (prev.includes(instrumentId)) {
        return prev.filter(id => id !== instrumentId);
      } else {
        return [...prev, instrumentId];
      }
    });
  };

  const handleSelectAll = () => {
    if (selectedInstruments.length === instruments.length) {
      setSelectedInstruments([]);
    } else {
      setSelectedInstruments(instruments.map(instrument => instrument._id));
    }
  };

  const handleExpandRow = (instrumentId) => {
    setExpandedRows(prev => {
      if (prev.includes(instrumentId)) {
        return prev.filter(id => id !== instrumentId);
      } else {
        return [...prev, instrumentId];
      }
    });
  };

  // Simple function to determine if dropdown should be positioned above
  const shouldPositionDropdownAbove = (instrumentId) => {
    if (showActionMenu !== instrumentId) return false;
    
    // Get all instrument rows
    const allRows = document.querySelectorAll('tbody tr:not(.expanded-row)');
    const currentRow = document.querySelector(`tr[data-instrument-id="${instrumentId}"]`);
    
    if (!currentRow || !allRows.length) return false;
    
    // Find the index of current row
    const currentIndex = Array.from(allRows).indexOf(currentRow);
    const totalRows = allRows.length;
    
    // Position above if we're in the last 3 rows
    return currentIndex >= totalRows - 3;
  };

  const handleQuickPresetApply = (instrument) => {
    setShowActionMenu(null);
    setShowQuickPresetModal(instrument);
  };

  // Bulk endpoint update handlers
  const handleBulkEndpointClick = () => {
    setIsBulkEndpointMode(true);
    setSelectedInstruments([]);
    setBulkEndpointValue('');
    setBulkEndpointError('');
  };

  const handleBulkEndpointCancel = () => {
    setIsBulkEndpointMode(false);
    setSelectedInstruments([]);
    setBulkEndpointValue('');
    setBulkEndpointError('');
  };

  const handleBulkEndpointUpdate = async () => {
    if (selectedInstruments.length === 0) {
      setBulkEndpointError('Please select at least one instrument');
      return;
    }

    if (!bulkEndpointValue.trim()) {
      setBulkEndpointError('Please enter an endpoint value');
      return;
    }

    // Confirm before proceeding
    setConfirmDialog({
      isOpen: true,
      title: 'Update Endpoints',
      message: `Are you sure you want to update the endpoint to "${bulkEndpointValue}" for ${selectedInstruments.length} selected instrument(s)?`,
      onConfirm: async () => {
        setConfirmDialog({ isOpen: false });
        
        const instrumentsToUpdate = instruments.filter(
          instrument => selectedInstruments.includes(instrument._id)
        );

        setRestoreProgress({
          isLoading: true,
          current: 0,
          total: instrumentsToUpdate.length,
          message: 'Starting endpoint update...'
        });

        let successCount = 0;
        let failedInstruments = [];

        for (const [index, instrument] of instrumentsToUpdate.entries()) {
          try {
            setRestoreProgress(prev => ({
              ...prev,
              current: index + 1,
              message: `Updating endpoint for ${instrument.symbol}...`
            }));

            await instrumentAPI.update(instrument.symbol, {
              endpoint: bulkEndpointValue.trim()
            });

            await new Promise(resolve => setTimeout(resolve, 100));
            successCount++;
          } catch (error) {
            console.error(`Error updating endpoint for ${instrument.symbol}:`, error);
            failedInstruments.push({
              symbol: instrument.symbol,
              reason: error.message || 'API error'
            });
          }
        }

        setTimeout(() => {
          if (failedInstruments.length > 0) {
            const failureMessage = failedInstruments
              .map(f => `${f.symbol}: ${f.reason}`)
              .join('\n');
            alert(
              `Endpoint update completed with some issues:\n` +
              `Successfully updated: ${successCount} instruments\n` +
              `Failed: ${failedInstruments.length} instruments\n\n` +
              `Failed instruments:\n${failureMessage}`
            );
          } else {
            alert(`Successfully updated endpoint for all ${successCount} instruments!`);
          }

          setRestoreProgress({
            isLoading: false,
            current: 0,
            total: 0,
            message: ''
          });
          
          // Reset bulk endpoint mode
          handleBulkEndpointCancel();
          onInstrumentUpdated();
        }, 500);
      }
    });
  };

  const instrumentsArray = Array.isArray(instruments) ? instruments : [];
  const sortedInstruments = [...instrumentsArray].sort((a, b) => {
    return getSymbolNumber(a.symbol) - getSymbolNumber(b.symbol);
  });

  return (
    <>
      {restoreProgress.isLoading && (
        <div className="modal-overlay">
          <div className="progress-dialog">
            <div className="progress-header">
              <h3>Processing...</h3>
            </div>
            <div className="progress-body">
              <div className="progress-bar">
                <div 
                  className="progress-bar-fill" 
                  style={{ width: `${(restoreProgress.current / restoreProgress.total) * 100}%` }}
                />
              </div>
              <p className="progress-message">{restoreProgress.message}</p>
              <p className="progress-count">
                {restoreProgress.current} / {restoreProgress.total}
              </p>
            </div>
          </div>
        </div>
      )}

      <div className="card">
        <div className="card-header">
          <div className="header-content">
            <h3 className="card-title">
              <i className="fas fa-chart-line" style={{ color: 'var(--admin-primary)' }}></i>
              Available Instruments
              <span className="badge">{sortedInstruments.length}</span>
            </h3>
            <div className="header-actions">
              {isBackupMode ? (
                <>
                  <button 
                    className="btn-secondary"
                    onClick={handleSelectAll}
                  >
                    {selectedInstruments.length === instruments.length ? (
                      <><i className="fas fa-times"></i> Deselect All</>
                    ) : (
                      <><i className="fas fa-check-square"></i> Select All</>
                    )}
                  </button>
                  <button 
                    className="btn-primary"
                    onClick={handleConfirmBackup}
                  >
                    <i className="fas fa-download"></i> Backup ({selectedInstruments.length})
                  </button>
                  <button 
                    className="btn-secondary"
                    onClick={() => {
                      setIsBackupMode(false);
                      setSelectedInstruments([]);
                    }}
                  >
                    Cancel
                  </button>
                </>
              ) : isBulkEndpointMode ? (
                <>
                  <button 
                    className="btn-secondary"
                    onClick={handleSelectAll}
                  >
                    {selectedInstruments.length === instruments.length ? (
                      <><i className="fas fa-times"></i> Deselect All</>
                    ) : (
                      <><i className="fas fa-check-square"></i> Select All</>
                    )}
                  </button>
                  <div className="bulk-endpoint-input-group">
                    <input
                      type="text"
                      className="bulk-endpoint-input"
                      placeholder="Enter new endpoint..."
                      value={bulkEndpointValue}
                      onChange={(e) => {
                        setBulkEndpointValue(e.target.value);
                        setBulkEndpointError('');
                      }}
                      onKeyPress={(e) => e.key === 'Enter' && handleBulkEndpointUpdate()}
                    />
                  </div>
                  <button 
                    className="btn-primary"
                    onClick={handleBulkEndpointUpdate}
                    disabled={restoreProgress.isLoading || selectedInstruments.length === 0}
                  >
                    <i className="fas fa-server"></i> Update ({selectedInstruments.length})
                  </button>
                  <button 
                    className="btn-secondary"
                    onClick={handleBulkEndpointCancel}
                    disabled={restoreProgress.isLoading}
                  >
                    Cancel
                  </button>
                </>
              ) : (
                <>
                  <button 
                    className="btn-secondary"
                    onClick={handleBackupClick}
                  >
                    <i className="fas fa-download"></i> Backup
                  </button>
                  <button 
                    className="btn-secondary"
                    onClick={handleBulkEndpointClick}
                  >
                    <i className="fas fa-server"></i> Bulk Endpoint
                  </button>
                  <span className="action-bar-divider"></span>
                  <button 
                    className="btn-secondary danger"
                    onClick={handleDeleteAll}
                  >
                    <i className="fas fa-trash"></i> Delete All
                  </button>
                </>
              )}
            </div>
          </div>
        </div>
        
        <div className="data-table-container">
          {/* Bulk endpoint error message */}
          {isBulkEndpointMode && bulkEndpointError && (
            <div className="bulk-endpoint-error">
              <i className="fas fa-exclamation-triangle"></i>
              {bulkEndpointError}
            </div>
          )}
          <table className="data-table">
            <thead>
              <tr>
                {(isBackupMode || isBulkEndpointMode) && <th style={{ width: '50px' }}>Select</th>}
                <th style={{ width: '50px' }}></th>
                <th>Symbol</th>
                <th>Description</th>
                <th>Type</th>
                <th>Contract Size</th>
                <th>Spread</th>
                <th>Market Status</th>
                <th style={{ width: '100px' }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {sortedInstruments.length === 0 ? (
                <tr>
                  <td colSpan={(isBackupMode || isBulkEndpointMode) ? 9 : 8} style={{ textAlign: 'center', padding: '3rem' }}>
                    <div className="empty-state-inline">
                      <i className="fas fa-inbox" style={{ fontSize: '2rem', color: 'var(--admin-text-light)', marginBottom: '1rem' }}></i>
                      <p>No instruments available</p>
                    </div>
                  </td>
                </tr>
              ) : (
                sortedInstruments.map(instrument => (
                  <React.Fragment key={instrument._id}>
                    <tr 
                      className={(isBackupMode || isBulkEndpointMode) && selectedInstruments.includes(instrument._id) ? 'selected' : ''}
                      data-instrument-id={instrument._id}
                    >
                      {(isBackupMode || isBulkEndpointMode) && (
                        <td>
                          <input
                            type="checkbox"
                            checked={selectedInstruments.includes(instrument._id)}
                            onChange={() => handleInstrumentSelect(instrument._id)}
                            onClick={(e) => e.stopPropagation()}
                          />
                        </td>
                      )}
                      <td>
                        <button 
                          className="expand-btn"
                          onClick={() => handleExpandRow(instrument._id)}
                          title={expandedRows.includes(instrument._id) ? 'Collapse details' : 'Expand details'}
                        >
                          <i className={`fas fa-chevron-${expandedRows.includes(instrument._id) ? 'up' : 'down'}`}></i>
                        </button>
                      </td>
                      <td>
                        <span className="instrument-value">{instrument.symbol}</span>
                      </td>
                      <td>
                        <span className="instrument-description">{instrument.description}</span>
                      </td>
                      <td>
                        <span className={`instrument-type ${instrument.type.toLowerCase()}`}>
                          {instrument.type}
                        </span>
                      </td>
                      <td>
                        <span className="instrument-value">{instrument.contract_Size}</span>
                      </td>
                      <td>
                        <span className={`spread-value ${!validateSpreadPipettes(instrument.spread_pipettes) ? 'spread-invalid' : ''}`}>
                          {instrument.spread_pipettes}
                          <span className="spread-unit">pipettes</span>
                        </span>
                      </td>
                      <td>
                        {getMarketStatusBadge(instrument.symbol)}
                      </td>
                      <td>
                        <div className="action-menu">
                          <button 
                            className="action-menu-btn"
                            onClick={(e) => {
                              e.stopPropagation();
                              setShowActionMenu(showActionMenu === instrument._id ? null : instrument._id);
                            }}
                          >
                            <i className="fas fa-ellipsis-v"></i>
                          </button>
                          
                          {showActionMenu === instrument._id && (
                            <div className={`action-menu-dropdown ${shouldPositionDropdownAbove(instrument._id) ? 'position-top' : ''}`}>
                              <button 
                                className="action-menu-item"
                                onClick={() => handleEdit(instrument)}
                              >
                                <i className="fas fa-edit"></i> Edit
                              </button>
                              <button 
                                className="action-menu-item"
                                onClick={() => setShowTradingHoursModal(instrument)}
                              >
                                <i className="fas fa-clock"></i> Trading Hours
                              </button>
                              {ENABLE_TRADING_HOURS_PRESETS && (
                                <button 
                                  className="action-menu-item"
                                  onClick={() => handleQuickPresetApply(instrument)}
                                >
                                  <i className="fas fa-magic"></i> Quick Preset
                                </button>
                              )}
                              <button 
                                className="action-menu-item danger"
                                onClick={() => handleDelete(instrument)}
                              >
                                <i className="fas fa-trash"></i> Delete
                              </button>
                            </div>
                          )}
                        </div>
                      </td>
                    </tr>
                    {expandedRows.includes(instrument._id) && (
                      <tr className="expanded-row">
                        <td colSpan={(isBackupMode || isBulkEndpointMode) ? 9 : 8}>
                          <div className="expanded-content">
                            {editingRows.includes(instrument._id) ? (
                              // Edit Mode
                              <div className="inline-edit-form">
                                <div className="edit-header">
                                  <h4>
                                    <i className="fas fa-edit" style={{ color: 'var(--admin-primary)' }}></i>
                                    Edit Instrument: {instrument.symbol}
                                  </h4>
                                  <div className="edit-actions">
                                    <button 
                                      className="btn-secondary btn-sm"
                                      onClick={() => handleCancelEdit(instrument._id)}
                                      disabled={isSubmitting}
                                    >
                                      <i className="fas fa-times"></i> Cancel
                                    </button>
                                    <button 
                                      className="btn-primary btn-sm"
                                      onClick={() => handleSaveEdit(instrument._id)}
                                      disabled={isSubmitting}
                                    >
                                      <i className="fas fa-save"></i> 
                                      {isSubmitting ? 'Saving...' : 'Save Changes'}
                                    </button>
                                  </div>
                                </div>

                                {editErrors[instrument._id] && (
                                  <div className="inline-error-message">
                                    <i className="fas fa-exclamation-triangle"></i>
                                    {editErrors[instrument._id]}
                                  </div>
                                )}

                                <div className="edit-form-grid">
                                  <div className="edit-section">
                                    <h5>Basic Information</h5>
                                    <div className="form-row">
                                      <div className="form-group">
                                        <label>Symbol</label>
                                        <input
                                          type="text"
                                          value={editFormData[instrument._id]?.symbol || ''}
                                          onChange={(e) => handleEditInputChange(instrument._id, 'symbol', e.target.value)}
                                          disabled={isSubmitting}
                                        />
                                      </div>
                                      <div className="form-group">
                                        <label>Description</label>
                                        <input
                                          type="text"
                                          value={editFormData[instrument._id]?.description || ''}
                                          onChange={(e) => handleEditInputChange(instrument._id, 'description', e.target.value)}
                                          disabled={isSubmitting}
                                        />
                                      </div>
                                    </div>
                                    <div className="form-row">
                                      <div className="form-group">
                                        <label>Type</label>
                                        <select
                                          value={editFormData[instrument._id]?.type || ''}
                                          onChange={(e) => handleEditInputChange(instrument._id, 'type', e.target.value)}
                                          disabled={isSubmitting}
                                        >
                                          <option value="Forex">Forex</option>
                                          <option value="Crypto">Crypto</option>
                                          <option value="Stocks">Stocks</option>
                                        </select>
                                      </div>
                                      <div className="form-group">
                                        <label>Contract Size</label>
                                        <input
                                          type="text"
                                          value={editFormData[instrument._id]?.contract_Size || ''}
                                          onChange={(e) => handleEditInputChange(instrument._id, 'contract_Size', e.target.value)}
                                          disabled={isSubmitting}
                                        />
                                      </div>
                                    </div>
                                  </div>

                                  <div className="edit-section">
                                    <h5>Order Parameters</h5>
                                    <div className="form-row">
                                      <div className="form-group">
                                        <label>Min Order Size</label>
                                        <input
                                          type="text"
                                          value={editFormData[instrument._id]?.min_Order_Size || ''}
                                          onChange={(e) => handleEditInputChange(instrument._id, 'min_Order_Size', e.target.value)}
                                          disabled={isSubmitting}
                                        />
                                      </div>
                                      <div className="form-group">
                                        <label>Max Order Size</label>
                                        <input
                                          type="text"
                                          value={editFormData[instrument._id]?.max_Order_Size || ''}
                                          onChange={(e) => handleEditInputChange(instrument._id, 'max_Order_Size', e.target.value)}
                                          disabled={isSubmitting}
                                        />
                                      </div>
                                    </div>
                                    <div className="form-row">
                                      <div className="form-group">
                                        <label>Order Quantity Step</label>
                                        <input
                                          type="text"
                                          value={editFormData[instrument._id]?.order_Quantity_Step || ''}
                                          onChange={(e) => handleEditInputChange(instrument._id, 'order_Quantity_Step', e.target.value)}
                                          disabled={isSubmitting}
                                        />
                                      </div>
                                      <div className="form-group">
                                        <label>Spread Pipettes</label>
                                        <input
                                          type="number"
                                          min="0"
                                          step="2"
                                          value={editFormData[instrument._id]?.spread_pipettes || ''}
                                          onChange={(e) => handleEditInputChange(instrument._id, 'spread_pipettes', e.target.value)}
                                          disabled={isSubmitting}
                                        />
                                      </div>
                                    </div>
                                  </div>

                                  <div className="edit-section">
                                    <h5>Price Parameters</h5>
                                    <div className="form-row-three">
                                      <div className="form-group">
                                        <label>Price Step</label>
                                        <input
                                          type="text"
                                          value={editFormData[instrument._id]?.price_Step || ''}
                                          onChange={(e) => handleEditInputChange(instrument._id, 'price_Step', e.target.value)}
                                          disabled={isSubmitting}
                                        />
                                      </div>
                                      <div className="form-group">
                                        <label>Pips Point</label>
                                        <input
                                          type="text"
                                          value={editFormData[instrument._id]?.pips_Point || ''}
                                          onChange={(e) => handleEditInputChange(instrument._id, 'pips_Point', e.target.value)}
                                          disabled={isSubmitting}
                                        />
                                      </div>
                                      <div className="form-group">
                                        <label>Decimals</label>
                                        <input
                                          type="text"
                                          value={editFormData[instrument._id]?.decimals || ''}
                                          onChange={(e) => handleEditInputChange(instrument._id, 'decimals', e.target.value)}
                                          disabled={isSubmitting}
                                        />
                                      </div>
                                    </div>
                                  </div>

                                  <div className="edit-section">
                                    <h5>Connection Details</h5>
                                    <div className="form-row">
                                      <div className="form-group">
                                        <label>Endpoint</label>
                                        <input
                                          type="text"
                                          value={editFormData[instrument._id]?.endpoint || ''}
                                          onChange={(e) => handleEditInputChange(instrument._id, 'endpoint', e.target.value)}
                                          disabled={isSubmitting}
                                        />
                                      </div>
                                      <div className="form-group">
                                        <label>Path (comma-separated)</label>
                                        <input
                                          type="text"
                                          value={editFormData[instrument._id]?.path || ''}
                                          onChange={(e) => handleEditInputChange(instrument._id, 'path', e.target.value)}
                                          placeholder="Enter paths separated by commas"
                                          disabled={isSubmitting}
                                        />
                                      </div>
                                    </div>
                                  </div>
                                </div>
                              </div>
                            ) : (
                              // View Mode
                              <div className="expanded-grid">
                                <div className="params-panel">
                                  <div className="params-panel-header">
                                    <i className="fas fa-shopping-cart"></i>
                                    <span>Order Parameters</span>
                                  </div>
                                  <div className="params-panel-body">
                                    <div className="param-row">
                                      <span className="param-label">Min Order Size</span>
                                      <span className="param-value">{instrument.min_Order_Size}</span>
                                    </div>
                                    <div className="param-row">
                                      <span className="param-label">Max Order Size</span>
                                      <span className="param-value">{instrument.max_Order_Size}</span>
                                    </div>
                                    <div className="param-row">
                                      <span className="param-label">Order Quantity Step</span>
                                      <span className="param-value">{instrument.order_Quantity_Step}</span>
                                    </div>
                                    <div className="param-row">
                                      <span className="param-label">Spread Pipettes</span>
                                      <span className="param-value">{instrument.spread_pipettes}</span>
                                    </div>
                                  </div>
                                </div>

                                <div className="params-panel">
                                  <div className="params-panel-header">
                                    <i className="fas fa-tag"></i>
                                    <span>Price Parameters</span>
                                  </div>
                                  <div className="params-panel-body">
                                    <div className="param-row">
                                      <span className="param-label">Price Step</span>
                                      <span className="param-value">{instrument.price_Step}</span>
                                    </div>
                                    <div className="param-row">
                                      <span className="param-label">Pips Point</span>
                                      <span className="param-value">{instrument.pips_Point}</span>
                                    </div>
                                    <div className="param-row">
                                      <span className="param-label">Decimals</span>
                                      <span className="param-value">{instrument.decimals}</span>
                                    </div>
                                  </div>
                                </div>

                                <div className="params-panel">
                                  <div className="params-panel-header">
                                    <i className="fas fa-plug"></i>
                                    <span>Connection Details</span>
                                  </div>
                                  <div className="params-panel-body">
                                    <div className="conn-block" style={{padding: '12px 16px'}}>
                                      <div className="conn-field">
                                        <span className="conn-label">Endpoint</span>
                                        <div className="conn-box">
                                          <div className="conn-endpoint">
                                            {instrument.endpoint || 'Not specified'}
                                          </div>
                                        </div>
                                      </div>
                                      <div className="conn-field">
                                        <span className="conn-label">Paths</span>
                                        <div className="conn-box">
                                          {Array.isArray(instrument.path) && instrument.path.length > 0 ? (
                                            instrument.path.map((p, i) => (
                                              <div key={i} className="conn-path-row">
                                                <span className="conn-path-bullet">●</span>
                                                <span>{p}</span>
                                              </div>
                                            ))
                                          ) : (
                                            <div className="conn-empty">No paths configured</div>
                                          )}
                                        </div>
                                      </div>
                                    </div>
                                  </div>
                                </div>
                              </div>
                            )}
                          </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      <ConfirmDialog
        isOpen={confirmDialog.isOpen}
        title={confirmDialog.title}
        message={confirmDialog.message}
        onConfirm={confirmDialog.onConfirm}
        onCancel={closeConfirmDialog}
      />

      {showTradingHoursModal && (
        <TradingHoursModal
          instrument={showTradingHoursModal}
          onClose={() => setShowTradingHoursModal(null)}
          onSave={onInstrumentUpdated}
        />
      )}

      {showQuickPresetModal && ENABLE_TRADING_HOURS_PRESETS && (
        <QuickPresetModal
          instrument={showQuickPresetModal}
          onClose={() => setShowQuickPresetModal(null)}
          onSave={onInstrumentUpdated}
        />
      )}

      {showActionMenu && (
        <div 
          className="action-menu-backdrop" 
          onClick={() => setShowActionMenu(null)}
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            zIndex: 40
          }}
        />
      )}
    </>
  );
}

export default InstrumentList;